import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { toast } from "sonner";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    company: "",
    phone: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé avec succès !");
      navigate("/");
    } catch (e) {
      console.error("Register error:", e);
      toast.error(e.response?.data?.detail || "Erreur lors de l'inscription");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C65D47] to-[#A84A38]">
          <img
            src="https://images.unsplash.com/photo-1542957057-debadce4ce81?w=1200&h=1600&fit=crop"
            alt="Products"
            className="w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Rejoignez MOREATI
            </h2>
            <p className="text-xl text-white/80">
              Votre partenaire en marketing engagé
            </p>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/abz6ktff_Copie%20de%20nouveau%20logo%20moreati%20%281%29.png" 
                alt="MOREATI" 
                className="h-14 object-contain"
              />
            </Link>
            <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
              Créer un compte
            </h1>
            <p className="text-stone-600 mt-2">
              Inscrivez-vous pour passer commande
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="Jean"
                  className="mt-2"
                  data-testid="register-firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Dupont"
                  className="mt-2"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="votre@email.com"
                className="mt-2"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Mot de passe *</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="company">Entreprise</Label>
              <Input
                id="company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Nom de votre entreprise"
                className="mt-2"
                data-testid="register-company-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                className="mt-2"
                data-testid="register-phone-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-12 text-lg font-semibold"
              data-testid="register-submit-btn"
            >
              {loading ? (
                "Création..."
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Créer mon compte
                </>
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-stone-600">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-[#C65D47] font-semibold hover:underline" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
